import { hasEmployeeUnreadCommentsInProcess } from '@core/document-comments-utils';
import { Pagination, UserProcess } from '@interfaces';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as commentsActions from '@state/comments/comments.actions';
import * as instructionItemsActions from '@state/instruction-items/instruction-items.actions';
import * as userProcessActions from '@state/user-process/user-process.actions';
import * as managementActions from './management.actions';
import { defaultPagination, initialState, ManagementState } from './management.state';

const reducer: ActionReducer<ManagementState, Action> = createReducer(
  initialState,
  on(
    userProcessActions.downloadFile,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(userProcessActions.downloadFileSuccess, (state): ManagementState => {
    return {
      ...state,
      loading: false,
      errorMessage: null,
    };
  }),
  on(userProcessActions.downloadFileError, (state): ManagementState => ({ ...state, loading: false })),
  on(
    managementActions.searchUsersProcesses,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.searchUsersProcessesSuccess,
    (state, { processes }): ManagementState => ({
      ...state,
      usersProcesses: processes.data,
      usersProcessesPagination: processes.pagination,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.searchUsersProcessesError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.searchProcessesOfUser,
    (state): ManagementState => ({
      ...state,
      loading: true,
      processesOfUser: null,
      processesOfUserPagination: { ...(defaultPagination as Pagination) },
      errorMessage: null,
    })
  ),
  on(
    managementActions.searchProcessesOfUserSuccess,
    (state, { processesOfUser }): ManagementState => ({
      ...state,
      processesOfUser: processesOfUser.data,
      processesOfUserPagination: processesOfUser.pagination,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.searchProcessesOfUserError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.getUserProcessBasicData,
    (state): ManagementState => ({
      ...state,
      userProcessBasicData: null,
      loadingBasicData: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessBasicDataSuccess,
    (state, { userProcessBasicData }): ManagementState => ({
      ...state,
      userProcessBasicData,
      loadingBasicData: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessBasicDataError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      userProcessBasicData: null,
      loadingBasicData: false,
      errorMessage,
    })
  ),

  on(
    managementActions.addNewUserProcess,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.addNewUserProcessSuccess,
    (state, { newUserProcess }): ManagementState => ({
      ...state,
      loading: false,
      user: {
        ...state.user,
        userProcesses: (state.user?.userProcesses?.length
          ? [...state.user.userProcesses, newUserProcess]
          : [newUserProcess]) as UserProcess[],
      },
      errorMessage: null,
    })
  ),
  on(
    managementActions.addNewUserProcessError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(
    managementActions.removeUserProcess,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(managementActions.removeUserProcessSuccess, (state, { removedUserProcessId }): ManagementState => {
    if (state.user === null) {
      return {
        ...state,
        loading: false,
        errorMessage: null,
      };
    }

    return {
      ...state,
      loading: false,
      user: {
        ...state.user,
        userProcesses: state.user.userProcesses.filter(({ id }) => id !== removedUserProcessId),
      },
      errorMessage: null,
    };
  }),
  on(
    managementActions.removeUserProcessError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(managementActions.restoreUserProcessSuccess, (state, { updatedUserProcess }): ManagementState => {
    if (state.user === null) {
      return {
        ...state,
        loading: false,
        errorMessage: null,
      };
    }

    return {
      ...state,
      loading: false,
      processesOfUser: state.processesOfUser.map(upInState => {
        return upInState.id === updatedUserProcess.id ? updatedUserProcess : upInState;
      }),
      user: {
        ...state.user,
        userProcesses: state.user.userProcesses.map(upInState => {
          return upInState.id === updatedUserProcess.id ? updatedUserProcess : upInState;
        }) as UserProcess[],
      },
      errorMessage: null,
    };
  }),
  on(
    managementActions.restoreUserProcessError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(
    managementActions.getUserProcess,
    (state): ManagementState => ({
      ...state,
      loading: true,
      userProcess: null,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessSuccess,
    (state, { userProcess }): ManagementState => ({
      ...state,
      userProcess,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.updateUserProcessBasicData,
    managementActions.updateUserProcessServices,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updateUserProcessBasicDataSuccess,
    managementActions.updateUserProcessServicesSuccess,
    (state, { updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: updatedUserProcess as UserProcess,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updateUserProcessBasicDataError,
    managementActions.updateUserProcessServicesError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(
    managementActions.setMainStayProcess,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.setMainStayProcessSuccess,
    (state, { userId, userProcessId, isMainStayProcessOfUser }): ManagementState => {
      // Only one process per user can be the main-stay, so when marking one we
      // must clear the flag on the user's other processes. We only touch the
      // isMainStayProcessOfUser field to avoid overwriting richer data in state.
      const reconcile = <T extends Partial<UserProcess>>(userProcess: T): T => {
        if (userProcess.id === userProcessId) {
          return { ...userProcess, isMainStayProcessOfUser };
        }
        if (isMainStayProcessOfUser && userProcess.userId === userId && userProcess.isMainStayProcessOfUser) {
          return { ...userProcess, isMainStayProcessOfUser: false };
        }
        return userProcess;
      };

      return {
        ...state,
        userProcess: state.userProcess ? reconcile(state.userProcess) : state.userProcess,
        usersProcesses: state.usersProcesses?.map(reconcile),
        processesOfUser: state.processesOfUser?.map(reconcile),
        loading: false,
        errorMessage: null,
      };
    }
  ),
  on(
    managementActions.setMainStayProcessError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage,
    })
  ),

  on(
    managementActions.getPersonalDetailsFullData,
    (state): ManagementState => ({
      ...state,
      loadingFullPersonalDetails: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getPersonalDetailsFullDataSuccess,
    (state, { personalDetails, userProcessId }): ManagementState => ({
      ...state,
      loadingFullPersonalDetails: false,
      errorMessage: null,
      userProcess:
        state.userProcess?.id === userProcessId ? { ...state.userProcess, personalDetails } : state.userProcess,
    })
  ),

  on(
    managementActions.getPersonalDetailsFullDataError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingFullPersonalDetails: false,
      errorMessage,
    })
  ),

  on(
    managementActions.getUserProcessDocuments,
    (state): ManagementState => ({
      ...state,
      loadingDocuments: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessDocumentsSuccess,
    (state, { processDocuments, userProcessId }): ManagementState => ({
      ...state,
      userProcessDocuments: {
        ...state.userProcessDocuments,
        userProcessId,
        processDocumentsList: processDocuments,
      },
      loadingDocuments: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessDocumentsError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loadingDocuments: false,
    })
  ),

  on(
    managementActions.updatePersonalDetails,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updatePersonalDetailsSuccess,
    (state, { updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: updatedUserProcess,
      usersProcesses: state.usersProcesses?.map(userProcess =>
        userProcess.id === updatedUserProcess.id ? updatedUserProcess : userProcess
      ),
      processesOfUser: state.processesOfUser?.map(userProcess =>
        userProcess.id === updatedUserProcess.id ? updatedUserProcess : userProcess
      ),
      loading: false,
      errorMessage: null,
    })
  ),

  on(
    managementActions.assignProcessType,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.assignProcessTypeSuccess,
    (state, { userProcess: updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: updatedUserProcess,
      usersProcesses: state.usersProcesses?.map(userProcessInstate =>
        userProcessInstate.id === updatedUserProcess.id ? updatedUserProcess : userProcessInstate
      ),
      processesOfUser: state.processesOfUser?.map(userProcessInState =>
        userProcessInState.id === updatedUserProcess.id ? updatedUserProcess : userProcessInState
      ),
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.assignProcessTypeError,
    (state): ManagementState => ({
      ...state,
      loading: false,
      errorMessage: 'ERROR_ASSIGNING_PROCESS_TYPE',
    })
  ),

  on(
    managementActions.getDetailsValidationConfig,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
      personalDetailsValidationConfig: null,
    })
  ),
  on(
    managementActions.getDetailsValidationConfigSuccess,
    (state, { userProcessId, all, members }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage: null,
      personalDetailsValidationConfig: { all, members, userProcessId },
    })
  ),
  on(
    managementActions.getDetailsValidationConfigError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.setPersonalDetailsVerification,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),

  on(
    managementActions.setPersonalDetailsVerificationSuccess,
    (state, { updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: updatedUserProcess,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updatePersonalDetailsError,
    managementActions.setPersonalDetailsVerificationError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.setDocumentsSigningMode,
    (state): ManagementState => ({
      ...state,
      loadingSigningMode: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.setDocumentsSigningModeSuccess,
    (state, { updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: updatedUserProcess,
      loadingSigningMode: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.setDocumentsSigningModeError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loadingSigningMode: false,
    })
  ),

  on(
    managementActions.changeProcessStatus,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.changeProcessStatusSuccess,
    (state, { updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: { ...updatedUserProcess },
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.changeProcessStatusError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.setUserDocumentStatus,
    managementActions.updateForeignerDocumentQuestionAnswer,
    (state): ManagementState => ({
      ...state,
      loadingDocuments: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.setUserDocumentStatusSuccess,
    (state, { updatedUserDocument }): ManagementState => ({
      ...state,
      userProcessDocuments: {
        ...state.userProcessDocuments,
        processDocumentsList: {
          ...state.userProcessDocuments.processDocumentsList,
          userDocs: state.userProcessDocuments.processDocumentsList.userDocs.map(userDoc => {
            return userDoc.id === updatedUserDocument.id ? updatedUserDocument : userDoc;
          }),
        },
      },
      loadingDocuments: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.setUserDocumentStatusError,
    managementActions.updateForeignerDocumentQuestionAnswerError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loadingDocuments: false,
    })
  ),

  on(
    managementActions.updateForeignerDocumentQuestionAnswerSuccess,
    (state, { updatedAnswer }): ManagementState => ({
      ...state,
      userProcessDocuments: {
        ...state.userProcessDocuments,
        processDocumentsList: {
          ...state.userProcessDocuments.processDocumentsList,
          userDocs: state.userProcessDocuments.processDocumentsList.userDocs.map(userDoc => {
            if (userDoc.id === updatedAnswer.userDocumentId) {
              return {
                ...userDoc,
                userDocumentAnswers: userDoc.userDocumentAnswers.map(userDocAnswer => {
                  return userDocAnswer.id === updatedAnswer.id
                    ? { ...userDocAnswer, answer: updatedAnswer.answer }
                    : userDocAnswer;
                }),
              };
            }
            return userDoc;
          }),
        },
      },
      loadingDocuments: false,
      errorMessage: null,
    })
  ),

  on(
    managementActions.getDocumentsGenerationStatus,
    (state): ManagementState => ({
      ...state,
      loadingDocuments: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getDocumentsGenerationStatusSuccess,
    (state, { status, userProcessId }): ManagementState => ({
      ...state,
      loadingDocuments: false,
      errorMessage: null,
      userProcessDocuments: {
        ...state.userProcessDocuments,
        userProcessId,
        documentsGenerationStatus: status,
      },
    })
  ),
  on(
    managementActions.getDocumentsGenerationStatusError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingDocuments: false,
      errorMessage,
    })
  ),

  on(
    managementActions.getPdfGenerationsSuccess,
    (state, { overview, userProcessId }): ManagementState => ({
      ...state,
      userProcessDocuments: {
        ...state.userProcessDocuments,
        userProcessId,
        pdfGenerations: overview,
      },
    })
  ),

  on(
    managementActions.getPdfGenerationCurrentProgressSuccess,
    (state, { progress, userProcessId }): ManagementState => ({
      ...state,
      userProcessDocuments: {
        ...state.userProcessDocuments,
        userProcessId,
        pdfGenerationCurrentProgress: progress,
      },
    })
  ),

  on(
    commentsActions.markDocumentCommentsAsReadSuccess,
    (state, { userProcessId, userDocumentId, updatedComments }): ManagementState => {
      if (state.userProcessDocuments.userProcessId !== userProcessId) {
        return { ...state };
      }

      const updatedUserDocs = state.userProcessDocuments.processDocumentsList.userDocs.map(userDoc => {
        return userDoc.id !== userDocumentId ? { ...userDoc } : { ...userDoc, documentComments: updatedComments };
      });

      return {
        ...state,
        userProcessDocuments: {
          ...state.userProcessDocuments,
          processDocumentsList: {
            processDocs: { ...state.userProcessDocuments.processDocumentsList.processDocs },
            userDocs: updatedUserDocs,
          },
        },
      };
    }
  ),
  on(commentsActions.readerAnyNewCommentsForceCheck, (state, { readerId, userProcessId }): ManagementState => {
    if (state.userProcessDocuments.userProcessId !== userProcessId) {
      return { ...state };
    }

    const userDocs = state.userProcessDocuments?.processDocumentsList?.userDocs;
    if (!userDocs?.length) {
      return { ...state };
    }

    return { ...state };
  }),

  on(
    managementActions.generateDocumentsNow,
    (state): ManagementState => ({
      ...state,
      loadingDocuments: true,
    })
  ),
  on(
    managementActions.generateDocumentsNowError,
    (state): ManagementState => ({
      ...state,
      loadingDocuments: false,
    })
  ),

  on(
    managementActions.getMergedFileData,
    managementActions.areDocsGenerated,
    (state): ManagementState => ({
      ...state,
      loadingFileData: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getMergedFileDataSuccess,
    managementActions.areDocsGeneratedSuccess,
    (state): ManagementState => ({
      ...state,
      loadingFileData: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getMergedFileDataError,
    managementActions.areDocsGeneratedError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingFileData: false,
      errorMessage,
    })
  ),

  on(
    managementActions.printAllDocumentsOfForeinger,
    (state): ManagementState => ({
      ...state,
      loading: true,
      loadingFileData: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.printAllDocumentsOfForeingerSuccess,
    (state): ManagementState => ({
      ...state,
      loading: false,
      loadingFileData: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.printAllDocumentsOfForeingerError,
    (state): ManagementState => ({
      ...state,
      loading: false,
      loadingFileData: false,
      errorMessage: 'ERROR-TRYING-TO-FETCH-MERGED-PDFS',
    })
  ),

  on(
    managementActions.printChecklist,
    (state): ManagementState => ({
      ...state,
      loading: true,
      loadingFileData: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.printChecklistSuccess,
    (state): ManagementState => ({
      ...state,
      loading: false,
      loadingFileData: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.printChecklistError,
    (state): ManagementState => ({
      ...state,
      loading: false,
      loadingFileData: false,
      errorMessage: 'ERROR-TRYING-TO-FETCH-CHECKLIST-PDF',
    })
  ),

  on(
    managementActions.getUserProcessChanges,
    (state): ManagementState => ({
      ...state,
      loadingUserProcessChanges: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserProcessChangesSuccess,
    (state, { userProcessId, userProcessChanges }): ManagementState => ({
      ...state,
      loadingUserProcessChanges: false,
      errorMessage: null,
      userProcessChanges: {
        userProcessId,
        changes: userProcessChanges,
      },
    })
  ),
  on(
    managementActions.getUserProcessChangesError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingUserProcessChanges: false,
      errorMessage,
    })
  ),

  on(
    managementActions.getUserActivity,
    (state): ManagementState => ({
      ...state,
      loadingUserActivity: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.getUserActivitySuccess,
    (state, { userActivity }): ManagementState => ({
      ...state,
      loadingUserActivity: false,
      errorMessage: null,
      userActivity: userActivity.data,
      userActivityPagination: userActivity.pagination,
    })
  ),
  on(
    managementActions.getUserActivityError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingUserActivity: false,
      errorMessage,
    })
  ),

  on(
    managementActions.setVisitDate,
    managementActions.deleteImmigrationOfficeVisitDate,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.setVisitDateSuccess,
    managementActions.deleteImmigrationOfficeVisitDateSuccess,
    (state, { updatedUserProcess }): ManagementState => ({
      ...state,
      loading: false,
      errorMessage: null,
      userProcess: { ...updatedUserProcess },
    })
  ),
  on(
    managementActions.setVisitDateError,
    managementActions.deleteImmigrationOfficeVisitDateError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),
  on(
    managementActions.confirmVoivoChange,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.confirmVoivoChangeSuccess,
    (state): ManagementState => ({
      ...state,
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.confirmVoivoChangeError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.updateDocEnabled,
    managementActions.updateDocEnabledBulk,
    (state): ManagementState => ({
      ...state,
      loadingProcess: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updateDocEnabledSuccess,
    (state): ManagementState => ({
      ...state,
      loadingProcess: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updateDocEnabledBulkSuccess,
    (state): ManagementState => ({
      ...state,
      loadingProcess: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updateDocEnabledError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loadingProcess: false,
    })
  ),
  on(instructionItemsActions.setUseCustomList, (state, { useCustomList, userProcessId }): ManagementState => {
    return {
      ...state,
      userProcess:
        state.userProcess.id === userProcessId
          ? { ...state.userProcess, usingCustomInstructionItemsList: useCustomList }
          : state.userProcess,
    };
  }),
  on(
    instructionItemsActions.setEnabledFeesSuccess,
    (state, { userProcessId, showFeesOnInstructionItemsList }): ManagementState => {
      return {
        ...state,
        userProcess:
          state.userProcess.id === userProcessId
            ? { ...state.userProcess, showFeesOnInstructionItemsList }
            : state.userProcess,
      };
    }
  ),

  on(
    managementActions.getItemsSentForFinalReview,
    (state): ManagementState => ({
      ...state,
      itemsSentForFinalReview: [],
      loadingItemsInReview: true,
    })
  ),
  on(
    managementActions.getItemsSentForFinalReviewSuccess,
    (state, { userAssets }): ManagementState => ({
      ...state,
      itemsSentForFinalReview: userAssets,
      loadingItemsInReview: false,
    })
  ),
  on(
    managementActions.getItemsSentForFinalReviewError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingItemsInReview: false,
      errorMessage,
    })
  ),
  on(
    managementActions.getDecisionDocuments,
    (state): ManagementState => ({
      ...state,
      decisionDocuments: [],
      loadingDecisionDocuments: true,
    })
  ),
  on(
    managementActions.getDecisionDocumentsSuccess,
    (state, { userAssets }): ManagementState => ({
      ...state,
      decisionDocuments: userAssets,
      loadingDecisionDocuments: false,
    })
  ),
  on(
    managementActions.getDecisionDocumentsError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      loadingDecisionDocuments: false,
      errorMessage,
    })
  ),
  on(
    managementActions.uploadDecisionSuccess,
    (state, { userProcess: updatedUserProcess }): ManagementState => ({
      ...state,
      userProcess: updatedUserProcess,
      usersProcesses: state.usersProcesses?.map(userProcessInState =>
        userProcessInState.id === updatedUserProcess.id ? updatedUserProcess : userProcessInState
      ),
    })
  ),
  on(
    managementActions.downloadItemSentForFinalReview,
    (state): ManagementState => ({
      ...state,
      loading: true,
    })
  ),
  on(
    managementActions.downloadItemSentForFinalReviewSuccess,
    (state, {}): ManagementState => ({
      ...state,
      loading: false,
    })
  ),
  on(
    managementActions.downloadItemSentForFinalReviewError,
    (state, {}): ManagementState => ({
      ...state,
      loading: false,
    })
  ),

  on(
    managementActions.updateDocumentsReviewComment,
    (state): ManagementState => ({
      ...state,
      loading: true,
      errorMessage: null,
    })
  ),
  on(
    managementActions.updateDocumentsReviewCommentSuccess,
    (state, { userProcess }): ManagementState => ({
      ...state,
      userProcess: { ...userProcess },
      loading: false,
      errorMessage: null,
    })
  ),
  on(
    managementActions.changeProcessStatusError,
    (state, { errorMessage }): ManagementState => ({
      ...state,
      errorMessage,
      loading: false,
    })
  ),

  on(
    managementActions.sendFollowUpEmail,
    (state): ManagementState => ({
      ...state,
      sendingEmail: true,
    })
  ),
  on(
    managementActions.sendFollowUpEmailSuccess,
    (state): ManagementState => ({
      ...state,
      sendingEmail: false,
    })
  ),
  on(
    managementActions.sendFollowUpEmailError,
    (state): ManagementState => ({
      ...state,
      sendingEmail: false,
    })
  ),

  on(
    managementActions.getProcessWithDocs,
    (state): ManagementState => ({
      ...state,
      loadingProcess: true,
    })
  ),
  on(
    managementActions.getProcessWithDocsSuccess,
    (state, { process }): ManagementState => ({
      ...state,
      processWithDocuments: process,
      loadingProcess: false,
    })
  ),
  on(
    managementActions.isAnyProcessOfMineMissingAssistant,
    (state): ManagementState => ({
      ...state,
      checkingMissingAssistantInProgress: true,
    })
  ),
  on(
    managementActions.isAnyProcessOfMineMissingAssistantSuccess,
    (state, { anyMissing }): ManagementState => ({
      ...state,
      checkingMissingAssistantInProgress: false,
      anyMyProcessesMissingAssistant: anyMissing,
    })
  ),
  on(
    managementActions.isAnyProcessOfMineMissingAssistantError,
    (state): ManagementState => ({
      ...state,
      checkingMissingAssistantInProgress: false,
    })
  )
);
export function managementReducer(state: ManagementState, action: Action): ManagementState {
  return reducer(state, action);
}
